import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, MessageSquare, Flag, Edit2, Trash2, MoreVertical, Wallet } from 'lucide-react';
import { formatRelativeTime } from './utils/timeUtils';
import CopyButton from './CopyButton';
import { getChain } from './chainUtils';
import { getAlias } from './aliasService';
import { getEmailUserDisplayInfo } from './walletService';
// COMMENTED OUT: Verified Interactor feature - too heavy (scans all transaction history)
// import { getTransactionHistory } from './etherscanService';
import { buildAddressUrl } from './urlUtils';
import OrgVerificationBadge from './components/OrgVerificationBadge';
import UserBadges from './components/UserBadges';
import { formatAddress, normalizeAddress, ADDRESS_MENTION_REGEX, isEmailUserIdentifier } from './addressUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// COMMENTED OUT: Verified Interactor feature - too heavy (scans all transaction history)
// VerificationModal component has been removed (previously at lines 14-235)

const Comment = ({
  comment,
  user,
  onReplyClick,    // Callback function to open reply modal
  onVote,
  onEdit,
  onDelete,
  onFlag,
  onLogin,
  userVote,
  isVerified = false,
  level = 0,
  targetAddress, // Added to pass target address for verification stats
  watchedAddresses = [], // Array of watched addresses
  showingInMentioned = false, // Is this being shown in "Mentioned In" tab?
  mentionedAddress = null, // The address being mentioned (current viewing address)
  dualVerification = null, // Object with withOriginal, withMentioned, originalAddress
  isHighlighted = false, // Whether this comment should be highlighted
  disableInteractions = false, // Disable voting and replying
  coinName = null, // Coin name for coin page comments
  coinId = null // Coin ID for coin page comments
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showVoters, setShowVoters] = useState(null); // 'upvotes' or 'downvotes'
  // COMMENTED OUT: Verified Interactor feature state variables
  // const [showVerificationModal, setShowVerificationModal] = useState(false);
  // const [verificationModalTarget, setVerificationModalTarget] = useState(null);
  const [authorAlias, setAuthorAlias] = useState(null);
  const [replyingToAlias, setReplyingToAlias] = useState(null);
  const [replyingToLoading, setReplyingToLoading] = useState(false);
  const [textAddressAliases, setTextAddressAliases] = useState({}); // Aliases for addresses in comment text
  const [emailAuthorDisplayInfo, setEmailAuthorDisplayInfo] = useState(null); // For email users: {username, displayName}
  // const [originalAddressAlias, setOriginalAddressAlias] = useState(null);
  // const [mentionedAddressAlias, setMentionedAddressAlias] = useState(null);

  // Check if author is an email user (authorAddress starts with 'e_')
  const isEmailAuthor = isEmailUserIdentifier(comment?.authorAddress);

  const isAuthor = user && (
    isEmailAuthor
      ? comment?.authorAddress === user.uid
      : comment?.authorAddress === user.address
  );
  const isDeleted = comment?.deleted;

  // Helper function to generate address markers
  const getAddressMarkers = (address) => {
    if (!address) return null;

    const markers = [];
    const normalizedAddress = address.toLowerCase();
    const normalizedUserAddress = user?.address?.toLowerCase();
    const normalizedWatchedAddresses = (watchedAddresses || []).filter(Boolean).map(addr => addr.toLowerCase());

    // Determine which address represents "This" - the current page being viewed
    // In "Mentioned In" tab, "This" should be the mentioned address (current page)
    // In regular "Comments" tab, "This" should be the target address (current page)
    const currentPageAddress = showingInMentioned ? mentionedAddress : targetAddress;
    const normalizedCurrentPage = currentPageAddress?.toLowerCase();

    // Check if it's the user's address
    if (user && normalizedAddress === normalizedUserAddress) {
      markers.push('You');
    }

    // Check if it's in watched addresses (but not the user's own address)
    // Note: User's own address is always in watched list, so we exclude it to avoid redundancy
    if (normalizedWatchedAddresses.includes(normalizedAddress) &&
        normalizedAddress !== normalizedUserAddress) {
      markers.push('Watched');
    }

    // Check if it's the current page address being viewed
    // This can combine with "You" to show "(You, This)" when viewing your own address
    if (normalizedCurrentPage && normalizedAddress === normalizedCurrentPage) {
      markers.push('This');
    }

    if (markers.length === 0) return null;

    return (
      <span className="ml-1 text-xs text-gray-500">
        ({markers.join(', ')})
      </span>
    );
  };

  // Load author alias (for wallet users only)
  useEffect(() => {
    const loadAuthorAlias = async () => {
      // Skip alias loading for email users (they use username instead)
      if (isEmailAuthor) {
        setAuthorAlias(null);
        return;
      }
      if (user && comment.authorAddress) {
        const authorChain = comment.authorChain || comment.chain || 'ethereum';
        const alias = await getAlias(user.address, comment.authorAddress, authorChain);
        setAuthorAlias(alias);
      }
    };
    loadAuthorAlias();
  }, [user, comment.authorAddress, comment.authorChain, comment.chain, isEmailAuthor]);

  // Load email author display info (username) for email users
  useEffect(() => {
    const loadEmailAuthorInfo = async () => {
      if (!isEmailAuthor) {
        setEmailAuthorDisplayInfo(null);
        return;
      }
      const info = await getEmailUserDisplayInfo(comment.authorAddress);
      setEmailAuthorDisplayInfo(info);
    };
    loadEmailAuthorInfo();
  }, [comment.authorAddress, isEmailAuthor]);

  // Load replying-to display name (alias for wallet users, username for email users)
  const isReplyingToEmailUser = isEmailUserIdentifier(comment.replyingToAddress);

  useEffect(() => {
    const loadReplyingToAlias = async () => {
      if (!comment.replyingToAddress) {
        setReplyingToAlias(null);
        setReplyingToLoading(false);
        return;
      }

      setReplyingToLoading(true);

      // For email users, get their username
      if (isReplyingToEmailUser) {
        const info = await getEmailUserDisplayInfo(comment.replyingToAddress);
        setReplyingToAlias(info?.username || null);
        setReplyingToLoading(false);
        return;
      }

      // For wallet users, get alias if user is logged in
      if (user) {
        const replyingToChain = comment.chain || 'ethereum';
        const alias = await getAlias(user.address, comment.replyingToAddress, replyingToChain);
        setReplyingToAlias(alias);
      } else {
        setReplyingToAlias(null);
      }
      setReplyingToLoading(false);
    };
    loadReplyingToAlias();
  }, [user, comment.replyingToAddress, comment.chain, isReplyingToEmailUser]);

  // Load aliases for addresses in comment text
  // Use stable chain value to prevent dependency array size changes
  const commentChain = comment.chain || 'ethereum';

  useEffect(() => {
    const loadTextAddressAliases = async () => {
      if (!user || !comment.text) {
        setTextAddressAliases({});
        return;
      }

      // Extract all Ethereum addresses from comment text
      const addressRegex = /\b0x[a-fA-F0-9]{40}\b/g;
      const addresses = comment.text.match(addressRegex) || [];

      if (addresses.length === 0) {
        setTextAddressAliases({});
        return;
      }

      // Load aliases for all found addresses
      const aliasesMap = {};
      await Promise.all(
        addresses.map(async (address) => {
          const normalizedAddress = address.toLowerCase();
          const alias = await getAlias(user.address, normalizedAddress, commentChain);
          if (alias) {
            aliasesMap[normalizedAddress] = alias;
          }
        })
      );

      setTextAddressAliases(aliasesMap);
    };

    loadTextAddressAliases();
  }, [user, comment.text, commentChain]);

  // COMMENTED OUT: Verified Interactor feature - load aliases for verification
  // // Load alias for dualVerification.originalAddress
  // useEffect(() => {
  //   const loadOriginalAddressAlias = async () => {
  //     if (user && dualVerification?.originalAddress) {
  //       const alias = await getAlias(user.address, dualVerification.originalAddress);
  //       setOriginalAddressAlias(alias);
  //     } else {
  //       setOriginalAddressAlias(null);
  //     }
  //   };
  //   loadOriginalAddressAlias();
  // }, [user, dualVerification?.originalAddress]);

  // // Load alias for mentionedAddress
  // useEffect(() => {
  //   const loadMentionedAddressAlias = async () => {
  //     if (user && mentionedAddress) {
  //       const alias = await getAlias(user.address, mentionedAddress);
  //       setMentionedAddressAlias(alias);
  //     } else {
  //       setMentionedAddressAlias(null);
  //     }
  //   };
  //   loadMentionedAddressAlias();
  // }, [user, mentionedAddress]);

  // Auto-close actions menu after 4 seconds of inactivity
  useEffect(() => {
    if (showActions) {
      const timer = setTimeout(() => {
        setShowActions(false);
      }, 4000); // 4 seconds

      return () => clearTimeout(timer);
    }
  }, [showActions]);

  // Early return if comment data is invalid (after all hooks)
  if (!comment || !comment.authorAddress) {
    return null;
  }

  // Generate avatar color from address or username
  const getAvatarColor = (identifier) => {
    if (!identifier) return '#6B7280';
    // For email users with username, generate color from username
    if (isEmailUserIdentifier(identifier) && emailAuthorDisplayInfo?.username) {
      const hash = emailAuthorDisplayInfo.username.slice(0, 6);
      let color = 0;
      for (let i = 0; i < hash.length; i++) {
        color = hash.charCodeAt(i) + ((color << 5) - color);
      }
      const hue = Math.abs(color) % 360;
      return `hsl(${hue}, 65%, 45%)`;
    }
    // For wallet addresses, use the existing hex-based color
    const hash = identifier.slice(2, 8);
    return `#${hash}`;
  };

  // Check if this is an AI-generated comment
  const isAIGenerated = comment.authorAddress === 'AI_Assistant';

  // Avatar color: Purple for AI, custom for email users, hex-based for wallet users
  const avatarColor = isAIGenerated
    ? '#8B5CF6'
    : isEmailAuthor
    ? getAvatarColor(comment.authorAddress)
    : getAvatarColor(comment.authorAddress);

  // Get display info for email author (X/Twitter style: "DisplayName @username")
  const getEmailAuthorDisplay = () => {
    if (!isEmailAuthor) return null;

    const displayName = emailAuthorDisplayInfo?.displayName;
    const username = emailAuthorDisplayInfo?.username;

    // If we have both, show "DisplayName @username" like X/Twitter
    if (displayName && username) {
      return (
        <span className="inline-flex items-baseline max-w-[200px] sm:max-w-[300px]">
          <span className="font-medium truncate" title={displayName}>{displayName}</span>
          <span className="text-gray-500 font-normal ml-1 flex-shrink-0">@{username}</span>
        </span>
      );
    }

    // If only username, show "@username"
    if (username) {
      return <span className="font-medium">@{username}</span>;
    }

    // If only displayName, show it
    if (displayName) {
      return <span className="font-medium">{displayName}</span>;
    }

    // Fallback: show truncated compositeId
    return <span className="font-medium">{comment.authorAddress.slice(0, 12)}...</span>;
  };

  // COMMENTED OUT: Verified Interactor feature helper function
  // const getVerificationDisplayName = (address, alias) => {
  //   if (alias) {
  //     return alias;
  //   }
  //   return formatAddress(address);
  // };

  // Handle vote button click
  const handleVote = (e, voteType) => {
    e.stopPropagation(); // Prevent triggering parent click handlers
    if (!user) {
      onLogin?.();
      return;
    }
    if (isDeleted) {
      return; // Prevent voting on deleted comments
    }
    onVote(comment, voteType);
  };

  // Handle reply button click
  const handleReplyClickInternal = (e) => {
    e.stopPropagation(); // Prevent triggering parent click handlers
    if (disableInteractions) {
      return; // Prevent replying on mentioned comments - user should click card to go to original
    }
    if (!user) {
      onLogin?.();
      return;
    }
    onReplyClick?.(comment);
  };

  // Handle flag button click
  const handleFlagClick = (e) => {
    e.stopPropagation(); // Prevent triggering parent click handlers
    if (!user) {
      onLogin?.();
      return;
    }
    onFlag(comment);
  };

  // Custom text renderer that detects addresses but renders them as plain text
  const renderTextWithAddresses = (text) => {
    // Match all cryptocurrency addresses using the same regex as parseMentions
    // Create a new RegExp instance to avoid state issues with global flag
    const addressRegex = new RegExp(ADDRESS_MENTION_REGEX.source, ADDRESS_MENTION_REGEX.flags);
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = addressRegex.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      const address = match[0];
      const normalizedAddress = normalizeAddress(address);
      const alias = textAddressAliases[normalizedAddress];
      const displayText = alias || address;

      // Render as plain text (no link)
      parts.push(
        <span
          key={`addr-${match.index}`}
          className="font-mono"
          title={alias ? `${alias} (${address})` : address}
        >
          {displayText}
          {getAddressMarkers(address)}
        </span>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <div className={`${level === 1 ? 'ml-6 sm:ml-12 mt-3' : 'mt-4'}`}>
      <div
        className={`bg-white rounded-lg border p-4 hover:shadow-sm transition ${
          isHighlighted
            ? 'border-yellow-400 bg-yellow-50 shadow-lg ring-2 ring-yellow-300'
            : 'border-gray-200'
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Avatar */}
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
              style={{ backgroundColor: avatarColor }}
              title={isAIGenerated ? 'AI-Generated Comment' : isEmailAuthor ? 'Email User' : 'Wallet User'}
            >
              {isAIGenerated ? (
                <span className="text-base">✨</span>
              ) : isEmailAuthor ? (
                // Email user: show first letter of displayName, then username, or 'E' as fallback
                emailAuthorDisplayInfo?.displayName
                  ? emailAuthorDisplayInfo.displayName[0].toUpperCase()
                  : emailAuthorDisplayInfo?.username
                  ? emailAuthorDisplayInfo.username[0].toUpperCase()
                  : 'E'
              ) : (
                // Wallet user: show first letter of ENS or address chars
                comment.authorENS ? comment.authorENS[0].toUpperCase() : comment.authorAddress?.slice(2, 4).toUpperCase()
              )}
            </div>

            {/* Author and timestamp */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Author display: AI Assistant, Email User, or Wallet User */}
                {isAIGenerated ? (
                  <span className="font-medium text-purple-600 flex items-center gap-1.5">
                    <span>AI Assistant</span>
                  </span>
                ) : isEmailAuthor ? (
                  // Email user: show "DisplayName @username" with mail icon (X/Twitter style)
                  <span className="text-gray-800 flex items-center gap-1.5">
                    {/* <Mail className="w-3.5 h-3.5 text-gray-500" title="Email User" /> */}
                      {getEmailAuthorDisplay()}
                  </span>
                ) : (
                  // Wallet user: show address with wallet icon and link
                  <>
                    <span className="flex items-center gap-1.5">
                      <Wallet className="w-3.5 h-3.5 text-gray-500" title="Wallet User" />
                      <Link
                        to={buildAddressUrl(comment.authorAddress, comment.chain)}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline break-words max-w-full inline-block"
                        title={`View ${comment.authorAddress}`}
                      >
                        {authorAlias || comment.authorENS || formatAddress(comment.authorAddress)}
                        {getAddressMarkers(comment.authorAddress)}
                      </Link>
                    </span>
                    <CopyButton text={comment.authorAddress} label="address" />
                  </>
                )}

                {/* COMMENTED OUT: Verified Interactor feature - too heavy (scans all transaction history) */}
                {/* {showingInMentioned && dualVerification ? (
                  <>
                    {dualVerification.withOriginal && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setVerificationModalTarget(dualVerification.originalAddress);
                          setShowVerificationModal(true);
                        }}
                        className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-200 transition cursor-pointer"
                        title={`Verified with ${getVerificationDisplayName(dualVerification.originalAddress, originalAddressAlias)}`}
                      >
                        <CheckCircle className="w-3 h-3" />
                        <span>Verified with {getVerificationDisplayName(dualVerification.originalAddress, originalAddressAlias)}</span>
                      </button>
                    )}
                    {dualVerification.withMentioned && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setVerificationModalTarget(mentionedAddress);
                          setShowVerificationModal(true);
                        }}
                        className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium hover:bg-green-200 transition cursor-pointer"
                        title={`Verified with ${getVerificationDisplayName(mentionedAddress, mentionedAddressAlias)}`}
                      >
                        <CheckCircle className="w-3 h-3" />
                        <span>Verified with {getVerificationDisplayName(mentionedAddress, mentionedAddressAlias)}</span>
                      </button>
                    )}
                  </>
                ) : (
                  isVerified && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setVerificationModalTarget(targetAddress);
                        setShowVerificationModal(true);
                      }}
                      className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-200 transition cursor-pointer"
                      title="Click to see interaction details"
                    >
                      <CheckCircle className="w-3 h-3" />
                      <span>Verified Interactor</span>
                    </button>
                  )
                )} */}

                {/* Organization Verification Badge (wallet users only) */}
                {!isEmailAuthor && (
                  <OrgVerificationBadge
                    address={comment.authorAddress}
                    variant="compact"
                    showRequestButton={false}
                    chain={comment.authorChain || comment.chain || 'ethereum'}
                  />
                )}

                {/* User Achievement Badges (wallet users only) */}
                {!isEmailAuthor && (
                  <UserBadges
                    address={comment.authorAddress}
                    variant="compact"
                    chain={comment.authorChain || comment.chain || 'ethereum'}
                  />
                )}

                {comment.edited && (
                  <span className="text-xs text-gray-500">(edited {formatRelativeTime(comment.updatedAt)})</span>
                )}

                <span className="text-sm text-gray-500">
                  {formatRelativeTime(comment.createdAt)}
                  {comment.targetAddress ? (
                    <span className="text-gray-400"> · <Link
                      to={buildAddressUrl(comment.targetAddress, comment.chain || comment.targetChain || 'ethereum')}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:text-blue-600 hover:underline"
                    >{getChain(comment.chain || comment.targetChain || 'ethereum')?.name || 'Ethereum'}</Link></span>
                  ) : comment.targetType === 'coin' && (coinId || comment.targetCoinId) ? (
                    <span className="text-gray-400"> · <Link
                      to={`/coin/${coinId || comment.targetCoinId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:text-blue-600 hover:underline"
                    >Coin {coinName || comment.targetCoinName || comment.targetCoinId}</Link></span>
                  ) : comment.chain || comment.targetChain ? (
                    <span className="text-gray-400"> · {getChain(comment.chain || comment.targetChain)?.name || comment.chain || comment.targetChain}</span>
                  ) : null}
                </span>
                </div>
              </div>
          </div>

          {/* Actions menu */}
          {isAuthor && !isDeleted && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowActions(!showActions);
                }}
                className="p-1 hover:bg-gray-100 rounded transition"
              >
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>

              {showActions && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(false);
                      onEdit(comment);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(false);
                      if (window.confirm('Are you sure you want to delete this comment?')) {
                        onDelete(comment);
                      }
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="mb-3">
          {isDeleted ? (
            <div className="text-gray-500 italic text-sm">
              [This comment has been deleted{comment.deletedAt ? ` ${formatRelativeTime(comment.deletedAt)}` : ''}]
            </div>
          ) : (
            <div className="text-gray-800 break-words prose prose-sm max-w-none">
              {/* Show @mention prefix for replies to other replies (not to top-level comments) */}
              {/* {level === 1 && console.log('Reply debug:', {
                level,
                parentId: comment.parentId,
                threadRootId: comment.threadRootId,
                replyingToAddress: comment.replyingToAddress,
                isReplyToReply: comment.parentId !== comment.threadRootId
              })} */}
              {level === 1 && comment.parentId && comment.threadRootId && comment.parentId !== comment.threadRootId && (
                comment.replyingToAddress === 'AI_Assistant' ? (
                  <span className="text-purple-600 font-medium">@AI Assistant </span>
                ) : isReplyingToEmailUser ? (
                  // Email user - show @username without link (no profile page for email users yet)
                  <span className="text-gray-600 font-medium">
                    @{replyingToLoading ? '...' : (replyingToAlias || 'user')}{' '}
                  </span>
                ) : (
                  // Wallet user - show link to address page
                  <><Link
                    to={buildAddressUrl(comment.replyingToAddress, comment.chain)}
                    className="text-blue-600 font-medium hover:underline break-words"
                  >
                    @{replyingToAlias || formatAddress(comment.replyingToAddress)}
                    {getAddressMarkers(comment.replyingToAddress)}
                  </Link>{' '}</>
                )
              )}
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Custom components for styling
                  // Process children for address detection
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h1: ({node, children, ...props}) => {
                    const processedChildren = typeof children === 'string' ? renderTextWithAddresses(children) : children;
                    return <h1 className="text-lg font-bold mt-2 mb-1" {...props}>{processedChildren}</h1>;
                  },
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h2: ({node, children, ...props}) => {
                    const processedChildren = typeof children === 'string' ? renderTextWithAddresses(children) : children;
                    return <h2 className="text-base font-bold mt-2 mb-1" {...props}>{processedChildren}</h2>;
                  },
                  // eslint-disable-next-line jsx-a11y/heading-has-content
                  h3: ({node, children, ...props}) => {
                    const processedChildren = typeof children === 'string' ? renderTextWithAddresses(children) : children;
                    return <h3 className="text-sm font-semibold mt-1 mb-1" {...props}>{processedChildren}</h3>;
                  },
                  p: ({node, children, ...props}) => {
                    // Process children to detect and format addresses
                    const processChildren = (child) => {
                      if (typeof child === 'string') {
                        return renderTextWithAddresses(child);
                      }
                      return child;
                    };

                    const processedChildren = Array.isArray(children)
                      ? children.map((child, idx) => <React.Fragment key={idx}>{processChildren(child)}</React.Fragment>)
                      : processChildren(children);

                    return <p className="mb-2 last:mb-0" {...props}>{processedChildren}</p>;
                  },
                  ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2" {...props} />,
                  li: ({node, children, ...props}) => {
                    const processChildren = (child) => {
                      if (typeof child === 'string') {
                        return renderTextWithAddresses(child);
                      }
                      return child;
                    };

                    const processedChildren = Array.isArray(children)
                      ? children.map((child, idx) => <React.Fragment key={idx}>{processChildren(child)}</React.Fragment>)
                      : processChildren(children);

                    // Use CSS to make p inside li inline to prevent line break between bullet and text
                    return <li className="mb-1 [&>p]:inline [&>p]:m-0" {...props}>{processedChildren}</li>;
                  },
                  a: ({node, href, children, ...props}) => {
                    // Don't process addresses inside links - keep link text as-is
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline break-all"
                        onClick={(e) => e.stopPropagation()}
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  },
                  pre: ({node, children, ...props}) => (
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded overflow-x-auto my-2" {...props}>
                      {children}
                    </pre>
                  ),
                  code: ({node, inline, className, children, ...props}) => {
                    // Inline code: single backticks `code`
                    // Block code: triple backticks ``` wrapped in <pre>
                    // Check parent node to determine if this is block code
                    const isBlock = node?.position?.start?.line !== node?.position?.end?.line || className?.startsWith('language-');

                    if (isBlock) {
                      // Block code - will be wrapped in <pre> by ReactMarkdown
                      return <code className="not-prose text-sm font-mono" {...props}>{children}</code>;
                    }
                    // Inline code - add not-prose to prevent typography plugin from adding backticks
                    return <code className="not-prose bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props}>{children}</code>;
                  },
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2" {...props} />
                  ),
                }}
              >
                {comment.text}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Actions bar */}
        <div className="flex items-center gap-4 text-sm flex-wrap">
          {/* Upvote */}
          <button
            onClick={(e) => handleVote(e, 'upvote')}
            className={`flex items-center gap-1 px-2 py-1 rounded transition ${
              isDeleted || disableInteractions
                ? 'opacity-50 cursor-not-allowed text-gray-400'
                : userVote === 'upvote'
                ? 'bg-green-100 text-green-700'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
            disabled={isDeleted || disableInteractions}
            onMouseEnter={() => !isDeleted && !disableInteractions && comment.upvotes > 0 && setShowVoters('upvotes')}
            onMouseLeave={() => setShowVoters(null)}
          >
            <ThumbsUp className="w-4 h-4" />
            <span className="font-medium">{comment.upvotes || 0}</span>

            {/* Upvoters tooltip */}
            {showVoters === 'upvotes' && comment.upvoters && comment.upvoters.length > 0 && (
              <div className="absolute bottom-full mb-2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg z-20 min-w-[150px]">
                <div className="font-semibold mb-1">Upvoted by:</div>
                {comment.upvoters.slice(0, 5).map((addr, idx) => (
                  <div key={idx}>{formatAddress(addr)}</div>
                ))}
                {comment.upvoters.length > 5 && (
                  <div className="text-gray-400 mt-1">...and {comment.upvoters.length - 5} more</div>
                )}
              </div>
            )}
          </button>

          {/* Downvote */}
          <button
            onClick={(e) => handleVote(e, 'downvote')}
            className={`flex items-center gap-1 px-2 py-1 rounded transition ${
              isDeleted || disableInteractions
                ? 'opacity-50 cursor-not-allowed text-gray-400'
                : userVote === 'downvote'
                ? 'bg-red-100 text-red-700'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
            disabled={isDeleted || disableInteractions}
            onMouseEnter={() => !isDeleted && !disableInteractions && comment.downvotes > 0 && setShowVoters('downvotes')}
            onMouseLeave={() => setShowVoters(null)}
          >
            <ThumbsDown className="w-4 h-4" />
            <span className="font-medium">{comment.downvotes || 0}</span>

            {/* Downvoters tooltip */}
            {showVoters === 'downvotes' && comment.downvoters && comment.downvoters.length > 0 && (
              <div className="absolute bottom-full mb-2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg z-20 min-w-[150px]">
                <div className="font-semibold mb-1">Downvoted by:</div>
                {comment.downvoters.slice(0, 5).map((addr, idx) => (
                  <div key={idx}>{formatAddress(addr)}</div>
                ))}
                {comment.downvoters.length > 5 && (
                  <div className="text-gray-400 mt-1">...and {comment.downvoters.length - 5} more</div>
                )}
              </div>
            )}
          </button>

          {/* Reply button */}
          {!isDeleted && (
            <div className="relative group">
              <button
                onClick={handleReplyClickInternal}
                className={`flex items-center gap-1 px-2 py-1 rounded transition ${
                  disableInteractions
                    ? 'opacity-50 cursor-not-allowed text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
                disabled={disableInteractions}
              >
                <MessageSquare className="w-4 h-4" />
                <span>Reply</span>
                {comment.replyCount > 0 && level === 0 && (
                  <span className="text-xs text-gray-500">({comment.replyCount})</span>
                )}
              </button>
              {disableInteractions && (
                <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-20 whitespace-nowrap">
                  Click the comment card to view on original page and reply there
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Flag button */}
          {!isDeleted && !isAuthor && !disableInteractions && (
            <button
              onClick={handleFlagClick}
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 text-gray-600 transition ml-auto flex-shrink-0"
            >
              <Flag className="w-4 h-4" />
              <span className="whitespace-nowrap">Report</span>
            </button>
          )}
        </div>

        {/* Flagged warning */}
        {comment.flagged && !comment.removed && (
          <div className="mt-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
            ⚠️ This comment has been flagged by multiple users
          </div>
        )}
      </div>

      {/* COMMENTED OUT: Verified Interactor feature - too heavy (scans all transaction history) */}
      {/* {showVerificationModal && verificationModalTarget && (
        <VerificationModal
          commenterAddress={comment.authorAddress}
          targetAddress={verificationModalTarget}
          user={user}
          onClose={() => {
            setShowVerificationModal(false);
            setVerificationModalTarget(null);
          }}
        />
      )} */}
    </div>
  );
};

export default Comment;
